import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import '../../assets/css/theme.css';
import '../../assets/css/landing.css';

interface FeatureItem {
  title: string;
  description: string;
  number: string;
  svg: ReactNode;
}

const featureData: FeatureItem[] = [
  {
    title: "Book An Appointment",
    description: "Reserve a personal styling session with our expert consultants, crafted around your taste and occasion.",
    number: "01",
    svg: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="8" width="26" height="23" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M5 15h26" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M12 5v6M24 5v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <rect x="11" y="20" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1"/>
        <rect x="21" y="20" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1"/>
      </svg>
    ),
  },
  {
    title: "Pick Up In Store",
    description: "Collect your curated pieces in-store, where our team ensures every detail is perfect before it leaves.",
    number: "02",
    svg: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 14l2.5-8h19L30 14" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
        <path d="M4 14h28v16a1 1 0 01-1 1H5a1 1 0 01-1-1V14z" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M14 31V22h8v9" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M4 14h8l-1 6H4M28 14h4v6h-7l-1-6h4z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Special Packaging",
    description: "Every order is dressed in our signature wrapping — a ceremony of texture, ribbon, and refined detail.",
    number: "03",
    svg: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="14" width="24" height="17" rx="1" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M4 14h28v4H4z" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M18 14v17" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M18 14c0 0-5-6-5-8s2-2 3 0 2 5 2 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M18 14c0 0 5-6 5-8s-2-2-3 0-2 5-2 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Free Global Returns",
    description: "Shop with complete confidence. Returns are seamless, free of charge, from anywhere in the world.",
    number: "04",
    svg: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="18" r="13" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M5 18h26" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M18 5c0 0-6 5-6 13s6 13 6 13M18 5c0 0 6 5 6 13s-6 13-6 13" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M23 13l-5 5 5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

// Ethno weave pattern as SVG bg — inspired by traditional Indian textile motifs
const Features = () => {
  const [visible, setVisible] = useState<number[]>([]);
  const refs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const observers = refs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible((prev) => [...new Set([...prev, i])]);
            obs.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
  }, []);

  return (
    <section className="ffh-feat">
      <div className="ffh-container">
        <div className="ffh-section-head">
          <p className="ffh-eyebrow">The Fashion Frame Promise</p>
          <h2 className="ffh-title">Crafted for Every <em>Detail</em></h2>
        </div>

        <div className="ffh-feat-grid">
          {featureData.map((f, i) => (
            <div
              key={f.title}
              ref={(el) => { refs.current[i] = el; }}
              className={`ffh-feat-card${visible.includes(i) ? ' is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <span className="ffh-feat-num" aria-hidden="true">{f.number}</span>
              <div className="ffh-feat-icon">{f.svg}</div>
              <h3 className="ffh-feat-title">{f.title}</h3>
              <p className="ffh-feat-desc">{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;